import { useState } from "react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import { CURRENCIES } from "@/lib/api";
import { Panel } from "@/components/common";

const COLORS = { GBP: "#0EA5E9", EUR: "#14B8A6", NGN: "#16A34A", KES: "#F59E0B", ZAR: "#EF4444", ZMW: "#A855F7", USD: "#4F46E5" };
const SWING = 5;

export function RateHistoryChart({ history }) {
  const [mode, setMode] = useState("index");
  const rows = [...history].sort((a, b) => a.month.localeCompare(b.month));
  const cur = Object.keys(CURRENCIES).filter((c) => c !== "USD");
  const usd = rows.map((h) => ({ month: h.month, ...Object.fromEntries(cur.map((c) => [c, h.rates[c] / h.rates.USD])) }));
  const data = mode === "index" ? usd.map((r) => ({ month: r.month, ...Object.fromEntries(cur.map((c) => [c, +((r[c] / usd[0][c]) * 100).toFixed(2)])) })) : usd;
  const swings = usd.slice(1).flatMap((r, i) => cur.map((c) => ({ c, month: r.month, pct: ((r[c] - usd[i][c]) / usd[i][c]) * 100 })).filter((s) => Math.abs(s.pct) > SWING));
  return (
    <Panel title="Rate history against USD" testid="fx-history-chart"
      action={<div className="flex rounded-md border text-xs">{[["index", "% change"], ["usd", "USD value"]].map(([k, l]) => <button key={k} data-testid={`fx-chart-mode-${k}`} onClick={() => setMode(k)} className={`px-2.5 py-1 transition-colors ${mode === k ? "bg-indigo-600 text-white" : "text-slate-600 hover:bg-slate-50"}`}>{l}</button>)}</div>}>
      {rows.length < 2 ? (
        <p className="text-sm text-slate-500" data-testid="fx-history-need-more">The chart appears once rates exist for two or more months ({rows.length} so far).</p>
      ) : (
        <>
          <p className="mb-2 text-xs text-slate-500">{mode === "index" ? `Each currency's USD value indexed to ${rows[0].month} = 100. Above 100 means the currency strengthened against USD.` : "Value of 1 unit in USD (log scale, so small and large currencies fit together)."}</p>
          <div className="h-72">
            <ResponsiveContainer>
              <LineChart data={data} margin={{ left: 8, right: 16 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} scale={mode === "index" ? "auto" : "log"} domain={["auto", "auto"]} allowDataOverflow width={70} />
                <Tooltip formatter={(v) => (mode === "index" ? v : Number(v).toPrecision(5))} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                {cur.map((c) => <Line key={c} type="monotone" dataKey={c} stroke={COLORS[c]} strokeWidth={2} dot={{ r: 3 }} />)}
              </LineChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-3 text-sm" data-testid="fx-swings">
            {swings.length ? swings.map((s) => <div key={s.c + s.month} className="text-amber-800" data-testid={`fx-swing-${s.c}-${s.month}`}>{s.c} moved {s.pct > 0 ? "+" : ""}{s.pct.toFixed(1)}% against USD in {s.month}</div>)
              : <div className="text-slate-500">No currency moved more than {SWING}% month-on-month.</div>}
          </div>
        </>
      )}
    </Panel>
  );
}
